import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import SearchableSelect from '../components/SearchableSelect';
import { FileText, Receipt, Calculator, ArrowsClockwise } from '@phosphor-icons/react';
import { Amount } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtRaw = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
// Privacy-aware money: respects global eye-toggle in header.
const M = ({ v }) => <Amount value={v} format={fmtRaw} />
const fmtDate = (s) => {
  if (!s) return '-';
  const [y, m, d] = String(s).slice(0, 10).split('-');
  return y ? `${d}/${m}/${y}` : s;
};

const todayYM = () => new Date().toISOString().slice(0, 7);
const todayY = () => new Date().toISOString().slice(0, 4);

export default function GstFilingPage() {
  const [activeTab, setActiveTab] = useState('gstr1'); // gstr1 | gstr3b
  const [filterMode, setFilterMode] = useState('month'); // month | year | range
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);

  // Filters
  const [companyId, setCompanyId] = useState('');
  const [clientId, setClientId] = useState('');
  const [month, setMonth] = useState(todayYM());
  const [year, setYear] = useState(todayY());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Report data
  const [r1, setR1] = useState(null);
  const [r3b, setR3b] = useState(null);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState('b2b'); // b2b | b2cs | b2cl | hsn

  useEffect(() => {
    (async () => {
      try {
        const [coR, clR] = await Promise.all([
          axios.get(`${API_URL}/api/companies`, { withCredentials: true }),
          axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
        ]);
        const gstCompanies = coR.data.filter((c) => c.gst_enabled);
        setCompanies(gstCompanies);
        if (gstCompanies.length === 1) setCompanyId(gstCompanies[0].id);
        setClients(clR.data || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const buildParams = () => {
    const p = {};
    if (companyId) p.company_id = companyId;
    if (clientId) p.client_id = clientId;
    if (filterMode === 'month') p.month = month;
    else if (filterMode === 'year') p.year = year;
    else if (filterMode === 'range') {
      if (fromDate) p.from_date = fromDate;
      if (toDate) p.to_date = toDate;
    }
    return p;
  };

  const runReport = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      if (activeTab === 'gstr1') {
        const r = await axios.get(`${API_URL}/api/gst/gstr1`, { params, withCredentials: true });
        setR1(r.data);
      } else {
        const r = await axios.get(`${API_URL}/api/gst/gstr3b`, { params, withCredentials: true });
        setR3b(r.data);
      }
    } catch (e) {
      console.error(e);
      alert('Failed to load report: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); /* eslint-disable-next-line */ }, [activeTab, companyId, clientId, filterMode, month, year, fromDate, toDate]);

  const clientOptions = useMemo(
    () => clients.map((c) => ({
      value: c.id,
      label: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company_name || '(unnamed)',
      sublabel: c.company_name || c.gstin || '',
      search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.gstin || ''}`,
    })),
    [clients]
  );

  return (
    <div data-testid="gst-filing-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>GST Filing</h1>
          <p className="text-sm" style={{ color: 'var(--dx-text2)' }}>
            Monthly GSTR-1 (outward sales) and GSTR-3B (consolidated tax payable) reports for GST-registered companies
          </p>
        </div>
        <Button variant="outline" onClick={runReport} data-testid="gst-refresh-btn">
          <ArrowsClockwise size={16} className="mr-2" />Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b" data-testid="gst-tabs">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === 'gstr1' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          onClick={() => setActiveTab('gstr1')}
          data-testid="tab-gstr1"
        >
          <FileText size={14} className="inline mr-1" />GSTR-1 (Outward Supplies)
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === 'gstr3b' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          onClick={() => setActiveTab('gstr3b')}
          data-testid="tab-gstr3b"
        >
          <Calculator size={14} className="inline mr-1" />GSTR-3B (Tax Summary)
        </button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Company</Label>
            <Select value={companyId || '__all'} onValueChange={(v) => setCompanyId(v === '__all' ? '' : v)}>
              <SelectTrigger data-testid="gst-co-select"><SelectValue placeholder="All GST companies" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All GST Companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {companies.length === 0 && (
              <p className="text-[10px] text-amber-600 mt-1">No GST-registered company found.</p>
            )}
          </div>

          {activeTab === 'gstr1' && (
            <div>
              <Label className="text-xs">Client (optional)</Label>
              <SearchableSelect
                testId="gst-client"
                value={clientId}
                onChange={setClientId}
                placeholder="All clients"
                options={clientOptions}
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Filter Mode</Label>
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger data-testid="gst-filter-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="year">Yearly</SelectItem>
                <SelectItem value="range">Date Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filterMode === 'month' && (
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="gst-month" />
            </div>
          )}
          {filterMode === 'year' && (
            <div>
              <Label className="text-xs">Year</Label>
              <Input type="number" min="2020" max="2099" value={year} onChange={(e) => setYear(e.target.value)} data-testid="gst-year" />
            </div>
          )}
          {filterMode === 'range' && (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="gst-from" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="gst-to" />
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Body */}
      {loading && <div className="text-sm text-gray-500" data-testid="gst-loading">Loading…</div>}

      {!loading && companies.length === 0 && (
        <Card className="p-8 text-center" data-testid="gst-no-companies">
          <div className="text-4xl mb-3">📋</div>
          <h3 className="font-semibold text-lg mb-2">No GST-registered company yet</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Open <span className="font-semibold">Companies</span> in the sidebar, edit your company,
            tick <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">GST Enabled</span>,
            and add the GSTIN + state. Only GST-enabled companies appear in GSTR reports.
          </p>
        </Card>
      )}

      {!loading && companies.length > 0 && activeTab === 'gstr1' && r1 && r1.totals.count === 0 && (
        <Card className="p-8 text-center" data-testid="gstr1-empty">
          <div className="text-4xl mb-3">🧾</div>
          <h3 className="font-semibold text-lg mb-2">No invoices in this period</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            You haven&apos;t raised any GST invoice for the selected filters yet.
            Once you create invoices for <span className="font-semibold">{companies.map((c) => c.name).join(', ')}</span>,
            they&apos;ll auto-appear here categorised as B2B / B2CS / B2CL with full CGST/SGST/IGST split.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => window.location.href = '/invoices'} data-testid="gstr1-empty-create">
            Go to Invoices →
          </Button>
        </Card>
      )}

      {activeTab === 'gstr1' && r1 && r1.totals.count > 0 && <Gstr1View data={r1} section={section} setSection={setSection} />}
      {activeTab === 'gstr3b' && r3b && <Gstr3BView data={r3b} />}
    </div>
  );
}

const StatCard = ({ label, value, sub, color = '#0F6E56' }) => (
  <Card className="p-4">
    <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--dx-text2)' }}>{label}</div>
    <div className="text-xl font-bold mt-1" style={{ color }}>{value}</div>
    {sub && <div className="text-[11px] mt-1" style={{ color: 'var(--dx-text2)' }}>{sub}</div>}
  </Card>
);

function Gstr1View({ data, section, setSection }) {
  const t = data.totals;
  const rows = data[section]?.rows || data.invoices;
  const showRows = section === 'hsn' ? null : rows;
  const hsn = data.hsn_summary || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Invoices" value={t.count} sub={`B2B ${data.b2b.count} · B2CS ${data.b2cs.count} · B2CL ${data.b2cl.count}`} color="#1F2937" />
        <StatCard label="Taxable Value" value={<M v={t.taxable} />} />
        <StatCard label="CGST" value={<M v={t.cgst} />} color="#1D4ED8" />
        <StatCard label="SGST" value={<M v={t.sgst} />} color="#7C3AED" />
        <StatCard label="IGST" value={<M v={t.igst} />} color="#DB2777" />
        <StatCard label="Invoice Total" value={<M v={t.total} />} color="#0F6E56" />
      </div>

      {/* Section selector */}
      <div className="flex gap-2 flex-wrap" data-testid="gstr1-sections">
        {[
          { k: 'b2b', label: `B2B (${data.b2b.count})` },
          { k: 'b2cs', label: `B2CS (${data.b2cs.count})` },
          { k: 'b2cl', label: `B2CL (${data.b2cl.count})` },
          { k: 'hsn', label: `HSN Summary (${hsn.length})` },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setSection(s.k)}
            className={`px-3 py-1.5 text-xs rounded-md border ${section === s.k ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium' : 'bg-white border-gray-200 text-gray-600'}`}
            data-testid={`gstr1-section-${s.k}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section !== 'hsn' ? (
        <Card className="dx-card">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'var(--dx-bg)' }}>
                  <TableHead>#</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Place of Supply</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">CGST</TableHead>
                  <TableHead className="text-right">SGST</TableHead>
                  <TableHead className="text-right">IGST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(showRows || []).length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-6 text-gray-400">No invoices in this section for the selected period.</TableCell></TableRow>
                ) : (
                  (showRows || []).map((r, i) => (
                    <TableRow key={r.invoice_id} data-testid={`gstr1-row-${r.invoice_id}`}>
                      <TableCell className="text-gray-400">{i + 1}</TableCell>
                      <TableCell className="font-medium">{r.invoice_number}</TableCell>
                      <TableCell>{fmtDate(r.invoice_date)}</TableCell>
                      <TableCell>{r.client_name}</TableCell>
                      <TableCell><span className="font-mono text-xs">{r.client_gstin || '-'}</span></TableCell>
                      <TableCell>{r.place_of_supply || '-'}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm ${r.is_interstate ? 'bg-pink-50 text-pink-700' : 'bg-indigo-50 text-indigo-700'}`}>
                          {r.is_interstate ? 'Inter-State' : 'Intra-State'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right"><M v={r.taxable} /></TableCell>
                      <TableCell className="text-right"><M v={r.cgst} /></TableCell>
                      <TableCell className="text-right"><M v={r.sgst} /></TableCell>
                      <TableCell className="text-right"><M v={r.igst} /></TableCell>
                      <TableCell className="text-right font-semibold"><M v={r.total} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card className="dx-card">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--dx-bg)' }}>
                <TableHead>#</TableHead>
                <TableHead>HSN / SAC</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Tax Rate</TableHead>
                <TableHead className="text-right">Taxable Value</TableHead>
                <TableHead className="text-right">Tax Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hsn.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-400">No HSN data.</TableCell></TableRow>
              ) : hsn.map((h, i) => (
                <TableRow key={h.hsn} data-testid={`gstr1-hsn-${i}`}>
                  <TableCell className="text-gray-400">{i + 1}</TableCell>
                  <TableCell className="font-mono">{h.hsn}</TableCell>
                  <TableCell>{h.description || '-'}</TableCell>
                  <TableCell className="text-right">{h.qty}</TableCell>
                  <TableCell className="text-right">{h.rate}%</TableCell>
                  <TableCell className="text-right"><M v={h.taxable} /></TableCell>
                  <TableCell className="text-right"><M v={h.tax} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function Gstr3BView({ data }) {
  const o = data.outward;
  const itc = data.itc;
  const n = data.net;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border-l-4 border-l-emerald-500">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">3.1 Outward Tax Liability (Sales)</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>Taxable Value</span><span className="font-medium"><M v={o.taxable} /></span></div>
            <div className="flex justify-between"><span>CGST</span><span className="font-medium"><M v={o.cgst} /></span></div>
            <div className="flex justify-between"><span>SGST</span><span className="font-medium"><M v={o.sgst} /></span></div>
            <div className="flex justify-between"><span>IGST</span><span className="font-medium"><M v={o.igst} /></span></div>
            <div className="flex justify-between border-t pt-1 mt-2 font-semibold">
              <span>Total Tax</span><span className="text-emerald-700"><M v={o.total_tax} /></span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">From {o.invoice_count} invoice(s)</div>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-l-indigo-500">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">4. Input Tax Credit (Purchases)</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>Taxable Value</span><span className="font-medium"><M v={itc.taxable} /></span></div>
            <div className="flex justify-between"><span>CGST</span><span className="font-medium"><M v={itc.cgst} /></span></div>
            <div className="flex justify-between"><span>SGST</span><span className="font-medium"><M v={itc.sgst} /></span></div>
            <div className="flex justify-between"><span>IGST</span><span className="font-medium"><M v={itc.igst} /></span></div>
            <div className="flex justify-between border-t pt-1 mt-2 font-semibold">
              <span>Total ITC</span><span className="text-indigo-700"><M v={itc.total} /></span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">From {itc.expense_count} GST-eligible expense(s)</div>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-l-rose-500 bg-rose-50/40">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">5. Net GST Payable</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>CGST</span><span className="font-medium"><M v={n.cgst} /></span></div>
            <div className="flex justify-between"><span>SGST</span><span className="font-medium"><M v={n.sgst} /></span></div>
            <div className="flex justify-between"><span>IGST</span><span className="font-medium"><M v={n.igst} /></span></div>
            <div className="flex justify-between border-t pt-2 mt-3 text-lg font-bold">
              <span>Pay</span><span className="text-rose-700"><M v={n.payable} /></span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">Output GST minus eligible ITC</div>
          </div>
        </Card>
      </div>

      {/* Sales breakdown */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Sales Breakdown by Category</h3>
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'var(--dx-bg)' }}>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Invoices</TableHead>
              <TableHead className="text-right">Taxable Value</TableHead>
              <TableHead className="text-right">Total Tax</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[
              ['B2B (Registered)', o.by_category.b2b],
              ['B2CS (Small Unregistered)', o.by_category.b2cs],
              ['B2CL (Large Unregistered)', o.by_category.b2cl],
            ].map(([label, v]) => (
              <TableRow key={label}>
                <TableCell className="font-medium">{label}</TableCell>
                <TableCell className="text-right">{v.count}</TableCell>
                <TableCell className="text-right"><M v={v.taxable} /></TableCell>
                <TableCell className="text-right"><M v={v.tax} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* ITC expenses */}
      {(itc.expenses || []).length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Receipt size={14} />Input Tax Credit — Eligible Expenses</h3>
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--dx-bg)' }}>
                <TableHead>#</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead className="text-right">Taxable</TableHead>
                <TableHead className="text-right">CGST</TableHead>
                <TableHead className="text-right">SGST</TableHead>
                <TableHead className="text-right">IGST</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itc.expenses.map((e, i) => (
                <TableRow key={e.id}>
                  <TableCell className="text-gray-400">{i + 1}</TableCell>
                  <TableCell>{fmtDate(e.date)}</TableCell>
                  <TableCell>{e.vendor_name || e.description || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{e.vendor_gstin || '-'}</TableCell>
                  <TableCell className="text-right"><M v={e.taxable_amount} /></TableCell>
                  <TableCell className="text-right"><M v={e.cgst} /></TableCell>
                  <TableCell className="text-right"><M v={e.sgst} /></TableCell>
                  <TableCell className="text-right"><M v={e.igst} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
